// O PERFIL NOVO (repaginação de 30/10/2026; com cara de Instagram em 31/10).
// A gramática que todo mundo já conhece, para o leitor novo se achar sem
// pensar: o @ no topo; a foto com os números ao lado (lidos, seguidores,
// seguindo); nome, bio e a sequência; "Editar perfil" e "Compartilhar"; os
// DESTAQUES — aqui, as trilhas em andamento, cada uma num círculo com o anel
// do progresso, e os Cadernos —; e a grade de capas em 3 colunas, por aba
// (lendo, lidos, quero ler). O resto (ritmo, protocolo, clubes, Círculo do
// Mentor, notificações, ajustes, suporte, convite) mora no menu.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BookV2, StreakData } from '../../types';
import { api, type TravessiaResumo } from '../../services/apiV2';
import { openExternalUrl } from '../platform';
import { Icon } from '../icons';
import { FolhaDoPerfil } from '../profile-social';
import { NvCapa } from './home-nova';
import { CardPro } from './pro';
import './nova.css';

type Aba = 'lendo' | 'lidos' | 'quero';
type Folha = null | 'editar' | 'compartilhar' | 'followers' | 'following';

export type MenuPerfil = {
  social: () => void; ajustes: () => void; cadernos: () => void; ritmo: () => void;
  protocolo: () => void; clubes: () => void; biblioteca: () => void; notificacoes: () => void;
  suporte: () => void; convidar: () => void;
  circulo?: (slug: string) => void; meuCirculo?: () => void;
};

const pctLivro = (b: BookV2) => (b.pages > 0 ? Math.min(100, Math.round(((b.read || 0) / b.pages) * 100)) : 0);
const pctTrilha = (t: TravessiaResumo) => (t.concluida ? 100 : t.total ? Math.min(100, Math.round(((t.pagina || 0) / t.total) * 100)) : 0);
/** O nome curto do destaque: o autor quando o título é "A sabedoria de X", senão a 1ª palavra forte. */
const rotuloCurto = (b: BookV2) => {
  const t = String(b.title || '').replace(/^(o|a|os|as|um|uma)\s+/i, '');
  const de = t.match(/\bde\s+(?:[A-ZÀ-Ú][\wÀ-ú.]*\s+)*([A-ZÀ-Ú][\wÀ-ú]+)$/);
  const r = de ? de[1] : t || String(b.title || '');
  return r.charAt(0).toUpperCase() + r.slice(1);
};
const numero = (n: number | null | undefined) => (n == null ? '–' : n >= 10000 ? `${Math.round(n / 1000)} mil` : n.toLocaleString('pt-BR'));

export const PerfilNovo: React.FC<{
  session: any;
  profile: any;
  books: BookV2[];
  streak: StreakData | null;
  travessias: Record<string, TravessiaResumo>;
  notifNaoLidas?: number;
  onAbrirLivro: (b: BookV2) => void;
  onTravessia: (b: BookV2) => void;
  onExplorar: () => void;
  menu: MenuPerfil;
  pro?: boolean;
  onPro?: () => void;
  onCadernos?: () => void;
  onAbrirPessoa?: (uid: string) => void;
  onPerfilMudou?: () => void;
  /** O aviso do teste grátis (bloqueio-pro.tsx): no teste, vai no lugar do "Torne-se Pro". */
  aviso?: React.ReactNode;
}> = ({ session, profile, books, streak, travessias, notifNaoLidas = 0, onAbrirLivro, onTravessia, onExplorar, menu, pro = true, onPro, onCadernos, onAbrirPessoa, onPerfilMudou, aviso }) => {
  const [aba, setAba] = useState<Aba>('lendo');
  const [menuAberto, setMenuAberto] = useState(false);
  const [folha, setFolha] = useState<Folha>(null);
  // O lado social (seguidores, seguindo, bio) vem do mesmo endpoint do perfil público.
  const [social, setSocial] = useState<any>(null);
  useEffect(() => {
    if (!profile?.id) return undefined;
    let vivo = true;
    api.getUserProfile(session, profile.id).then((d) => { if (vivo) setSocial(d); }).catch(() => {});
    return () => { vivo = false; };
  }, [session, profile?.id]);

  const grupos = useMemo(() => ({
    lendo: books.filter((b) => b.status === 'reading' || b.status === 'paused'),
    lidos: books.filter((b) => b.status === 'finished'),
    quero: books.filter((b) => b.status === 'wishlist'),
  }), [books]);
  const comMapa = books.filter((b) => travessias[b.id] && b.status !== 'wishlist');
  // Destaques: as trilhas em andamento primeiro, as concluídas depois.
  const destaques = [...comMapa].sort((a, b) => Number(!!travessias[a.id].concluida) - Number(!!travessias[b.id].concluida));
  const desafios = comMapa.reduce((s, b) => s + (travessias[b.id].desafios_feitos || 0), 0);
  const nome = social?.name || profile?.display_name || profile?.username || 'Leitor';
  const usuario = social?.username || profile?.username;
  const avatar = social?.avatarUrl || profile?.avatar_url;
  const bio = social?.bio ?? profile?.bio;
  const dias = streak?.current || 0;
  const lista = grupos[aba];
  // Conta zerada (0 lidos · 0 seguidores · 0 seguindo): três zeros em destaque
  // não dizem nada. No lugar, o que ela está lendo, que leva de volta à trilha.
  // Qualquer número acima de zero devolve a linha de números.
  const zerada = grupos.lidos.length === 0 && !(social?.followers > 0) && !(social?.following > 0);
  const lendoAgora = destaques.find((b) => !travessias[b.id].concluida && b.status !== 'finished') || grupos.lendo[0] || null;
  const dadosFolha = social || { id: profile?.id, name: nome, username: usuario, avatarUrl: avatar, bio, isMe: true };

  return (
    <div className="nv nv-perfil" data-tela="perfil-novo">
      <div className="nv-perfil-barra nv-entra">
        <span className="nv-perfil-usuario" data-perfil-usuario>
          {usuario ? <>@{usuario}</> : nome}
          {pro && <span className="nv-perfil-selo" title="Forjamente Pro" data-selo-pro><Icon name="sparkle" size={12} /></span>}
        </span>
        <button className="nv-icone-btn" onClick={() => setFolha('compartilhar')} aria-label="Compartilhar perfil" data-compartilhar-topo>
          <Icon name="share" size={19} />
        </button>
        <button className="nv-icone-btn" onClick={() => setMenuAberto(true)} aria-label="Menu" data-menu-perfil>
          <Icon name="menu" size={20} />
          {notifNaoLidas > 0 && <i className="nv-icone-ponto" />}
        </button>
      </div>

      <div className="nv-perfil-cabeca nv-entra nv-entra-2">
        <button className={`nv-avatar-g ${pro ? 'pro' : ''}`} onClick={() => setFolha('editar')} aria-label="Editar a foto e o perfil">
          <span>{avatar ? <img src={avatar} alt="" /> : String(nome).slice(0, 1).toUpperCase()}</span>
        </button>
        {zerada ? (
          <button className="nv-perfil-lendo" data-lendo-agora
            onClick={() => (lendoAgora ? (travessias[lendoAgora.id] ? onTravessia(lendoAgora) : onAbrirLivro(lendoAgora)) : onExplorar())}>
            {lendoAgora ? <>
              <span className="nv-mono">Lendo agora</span>
              <b>{lendoAgora.title}</b>
              <span>{lendoAgora.read > 0 ? `p. ${lendoAgora.read}` : 'começando'} <Icon name="arrow-right" size={13} /></span>
            </> : <>
              <span className="nv-mono">Comece por aqui</span>
              <b>Escolha a sua primeira trilha</b>
              <span>Explorar <Icon name="arrow-right" size={13} /></span>
            </>}
          </button>
        ) : (
        <div className="nv-perfil-numeros" data-numeros-perfil>
          <button onClick={() => setAba('lidos')} data-numero="lidos"><b>{numero(grupos.lidos.length)}</b><span>{grupos.lidos.length === 1 ? 'lido' : 'lidos'}</span></button>
          <button onClick={() => social && setFolha('followers')} data-numero="seguidores"><b>{numero(social?.followers)}</b><span>{social?.followers === 1 ? 'seguidor' : 'seguidores'}</span></button>
          <button onClick={() => social && setFolha('following')} data-numero="seguindo"><b>{numero(social?.following)}</b><span>seguindo</span></button>
        </div>
        )}
      </div>

      <div className="nv-perfil-bio nv-entra nv-entra-2">
        <h1>{nome}</h1>
        {bio ? <p>{bio}</p> : <button className="nv-perfil-sembio" onClick={() => setFolha('editar')} data-sem-bio>+ Escreva uma bio: o que você gosta de ler</button>}
        <div className="nv-perfil-marcas nv-mono" data-desafios-feitos={desafios}>
          <span className={dias ? 'aceso' : ''}><Icon name="flame" size={12} />{dias} {dias === 1 ? 'dia seguido' : 'dias seguidos'}</span>
          <span><Icon name="target" size={12} />{desafios} {desafios === 1 ? 'desafio feito' : 'desafios feitos'}</span>
        </div>
      </div>

      <div className="nv-perfil-botoes nv-entra nv-entra-3">
        <button onClick={() => setFolha('editar')} data-editar-perfil>Editar perfil</button>
        <button onClick={() => setFolha('compartilhar')} data-compartilhar-perfil>Compartilhar perfil</button>
      </div>

      {onPro && (pro ? (
        <div className="nv-mono nv-perfil-pro-ativo nv-entra nv-entra-3" data-perfil-pro-ativo>
          <Icon name="sparkle" size={12} /> Forjamente Pro · todas as trilhas abertas
        </div>
      ) : <div className="nv-entra nv-entra-3">{aviso || <CardPro onAbrir={onPro} onde="perfil" compacto />}</div>)}

      <div className="nv-destaques nv-entra nv-entra-3" data-minhas-travessias role="list" aria-label="Trilhas e cadernos">
        {destaques.map((b) => {
          const t = travessias[b.id];
          const p = pctTrilha(t);
          return (
            <button key={b.id} role="listitem" className={`nv-destaque ${t.concluida ? 'feita' : ''}`} onClick={() => onTravessia(b)}
              data-travessia-andamento={t.concluida ? undefined : b.id} data-travessia-concluida={t.concluida ? b.id : undefined}
              aria-label={`Travessia de ${b.title}: ${t.concluida ? 'concluída' : `${p}%${t.etapa ? `, etapa ${t.etapa.n}/${t.etapas}` : ''}`}`}>
              <span className="nv-destaque-anel" style={{ ['--p' as any]: `${p * 3.6}deg` }}>
                <span className="nv-destaque-miolo"><NvCapa book={b} w={0} h={0} /></span>
              </span>
              <small>{rotuloCurto(b)}</small>
              <em className="nv-mono">{t.concluida ? 'concluída' : `${p}%`}</em>
            </button>
          );
        })}
        {onCadernos && (
          <button role="listitem" className="nv-destaque icone" onClick={onCadernos} data-atalho-cadernos aria-label="Cadernos: os das trilhas e as suas notas">
            <span className="nv-destaque-anel"><span className="nv-destaque-miolo"><Icon name="book" size={24} /></span></span>
            <small>Cadernos</small>
            <em className="nv-mono">notas</em>
          </button>
        )}
        <button role="listitem" className="nv-destaque icone novo" onClick={onExplorar} data-destaque-nova aria-label="Começar uma trilha nova">
          <span className="nv-destaque-anel"><span className="nv-destaque-miolo"><Icon name="plus" size={24} /></span></span>
          <small>{destaques.length ? 'Nova trilha' : 'Trilhas'}</small>
          <em className="nv-mono">explorar</em>
        </button>
      </div>

      <section className="nv-perfil-grade-secao" data-minha-biblioteca>
        <div className="nv-perfil-abas" role="tablist" aria-label="Minha biblioteca">
          {([['lendo', 'Lendo', 'book'], ['lidos', 'Lidos', 'check'], ['quero', 'Quero ler', 'bookmark']] as [Aba, string, string][]).map(([k, r, ic]) => (
            <button key={k} role="tab" aria-selected={aba === k} onClick={() => setAba(k)} data-aba-biblioteca={k}>
              <Icon name={ic as any} size={17} /><span>{r}</span><small>{grupos[k].length}</small>
            </button>
          ))}
        </div>
        {lista.length === 0 ? (
          <p className="nv-vazio nv-perfil-vazio">{aba === 'quero' ? 'Nada na lista ainda. ' : aba === 'lidos' ? 'Nenhum livro terminado ainda.' : 'Nenhum livro em andamento. '}{aba !== 'lidos' && <button className="nv-link" onClick={onExplorar}>Explorar as trilhas</button>}</p>
        ) : (
          <div className="nv-perfil-grade" data-estante>
            {lista.map((b) => {
              const p = aba === 'lendo' ? pctLivro(b) : 0;
              return (
                <button key={b.id} onClick={() => onAbrirLivro(b)} data-livro-estante={b.id} aria-label={b.title}>
                  <NvCapa book={b} w={0} h={0} />
                  {travessias[b.id] && <span className="nv-grade-selo" title="Com trilha"><Icon name="globe" size={11} /></span>}
                  {aba === 'lendo' && <span className="nv-grade-barra" aria-hidden="true"><span style={{ transform: `scaleX(${p / 100})` }} /></span>}
                </button>
              );
            })}
            {aba !== 'lidos' && (
              <button className="nv-grade-mais" onClick={onExplorar} data-grade-adicionar aria-label="Adicionar um livro">
                <Icon name="plus" size={22} /><span className="nv-mono">adicionar</span>
              </button>
            )}
          </div>
        )}
      </section>

      {menuAberto && <MenuDoPerfil session={session} profile={profile} notifNaoLidas={notifNaoLidas} menu={menu} onFechar={() => setMenuAberto(false)} />}
      {folha && (
        <FolhaDoPerfil
          tipo={folha === 'followers' || folha === 'following' ? 'pessoas' : folha}
          pessoas={folha === 'followers' || folha === 'following' ? folha : undefined}
          session={session}
          data={dadosFolha}
          onClose={() => setFolha(null)}
          onSaved={(patch: any) => { setSocial((d: any) => ({ ...(d || dadosFolha), ...patch })); onPerfilMudou && onPerfilMudou(); }}
          onOpenProfile={(uid: string) => { setFolha(null); onAbrirPessoa && onAbrirPessoa(uid); }}
          onFollowDelta={(delta: number) => setSocial((d: any) => (d ? { ...d, following: Math.max(0, (d.following || 0) + delta) } : d))}
        />
      )}
    </div>
  );
};

const MenuDoPerfil: React.FC<{ session: any; profile: any; notifNaoLidas: number; menu: MenuPerfil; onFechar: () => void }> = ({ session, profile, notifNaoLidas, menu, onFechar }) => {
  const [mentores, setMentores] = useState<any[]>([]);
  useEffect(() => {
    if (!menu.circulo) return;
    let vivo = true;
    api.getMentores(session).then((r: any) => { if (vivo) setMentores((r?.mentores || []).filter((m: any) => !m.sou_o_mentor)); }).catch(() => {});
    return () => { vivo = false; };
  }, [session, menu.circulo]);
  const ir = (f?: () => void) => () => { onFechar(); f && f(); };
  const Item: React.FC<{ rotulo: string; dica?: string; ponto?: number; onClick?: () => void; dado?: string }> = ({ rotulo, dica, ponto, onClick, dado }) => (
    <button className="nv-folha-item" onClick={ir(onClick)} data-menu-item={dado}>
      <span>{rotulo}</span>
      {ponto ? <span className="nv-ponto">{ponto}</span> : dica ? <small>{dica}</small> : <Icon name="chevron-right" size={16} color="var(--ink-4)" />}
    </button>
  );
  // No body, por portal: dentro da tela, a folha ficava num contexto de
  // empilhamento abaixo da barra de abas, que encobria o fim do menu.
  return createPortal(
    <div className="nv-portal">
      <div className="nv-veu" onClick={onFechar} />
      <div className="nv-folha" role="dialog" aria-label="Menu do perfil" data-folha-menu>
        <div className="nv-folha-alca" />
        <span className="nv-mono">Você</span>
        <Item rotulo="Notificações" ponto={notifNaoLidas} onClick={menu.notificacoes} dado="notificacoes" />
        <Item rotulo="Perfil público: atividade e clubes" onClick={menu.social} dado="social" />
        <Item rotulo="Convidar amigos" onClick={menu.convidar} dado="convidar" />
        <span className="nv-mono">Leitura</span>
        <Item rotulo="Cadernos de estudo" onClick={menu.cadernos} dado="cadernos" />
        <Item rotulo="Ritmo e estatísticas" onClick={menu.ritmo} dado="ritmo" />
        <Item rotulo="Protocolo de 90 dias" onClick={menu.protocolo} dado="protocolo" />
        <Item rotulo="Clubes do livro" onClick={menu.clubes} dado="clubes" />
        <Item rotulo="Biblioteca completa e estantes" onClick={menu.biblioteca} dado="biblioteca" />
        {((menu.meuCirculo && profile?.is_mentor) || mentores.length > 0) && <span className="nv-mono">Círculo do Mentor</span>}
        {menu.meuCirculo && profile?.is_mentor && <Item rotulo="Meu Círculo" onClick={menu.meuCirculo} dado="meu-circulo" />}
        {menu.circulo && mentores.map((m) => <Item key={m.slug} rotulo={`Círculo de ${String(m.nome || '').trim().split(/\s+/)[0]}`} onClick={() => menu.circulo!(m.slug)} dado={`circulo-${m.slug}`} />)}
        <span className="nv-mono">Conta</span>
        <Item rotulo="Ajustes, aparência e plano" onClick={menu.ajustes} dado="ajustes" />
        <Item rotulo="Suporte" onClick={menu.suporte} dado="suporte" />
        <Item rotulo="Instagram @forjamente.1" dica="abrir" onClick={() => openExternalUrl('https://www.instagram.com/forjamente.1')} dado="instagram" />
      </div>
    </div>,
    document.body,
  );
};
